import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { configureApiAuth } from '@/shared/api/api-client';
import { ApiError } from '@/shared/api/api-error';
import * as authService from '../services/auth.service';
import { AuthContext } from './auth-context';
import type {
  AuthTokens,
  CurrentUser,
  LoginInput,
  RegisterInput,
} from '../models/auth.types';

export const REFRESH_TOKEN_KEY = 'workerize.auth.refreshToken';

let refreshInFlight: Promise<AuthTokens | null> | null = null;

function readRefreshToken(): string | null {
  if (typeof window === 'undefined') return null;
  return window.sessionStorage.getItem(REFRESH_TOKEN_KEY);
}

function writeRefreshToken(token: string | null): void {
  if (typeof window === 'undefined') return;
  if (token) {
    window.sessionStorage.setItem(REFRESH_TOKEN_KEY, token);
  } else {
    window.sessionStorage.removeItem(REFRESH_TOKEN_KEY);
  }
}

function errorMessage(error: unknown): string {
  if (error instanceof ApiError) {
    return error.message;
  }
  if (error instanceof Error) {
    return error.message;
  }
  return String(error);
}

async function rotateRefreshToken(refreshToken: string): Promise<AuthTokens | null> {
  if (refreshInFlight === null) {
    refreshInFlight = authService.refresh(refreshToken).catch(() => null);
    refreshInFlight.finally(() => {
      refreshInFlight = null;
    });
  }

  return refreshInFlight;
}

export function AuthProvider({ children }: { children: ReactNode }): ReactNode {
  const accessTokenRef = useRef<string | null>(null);
  const [user, setUser] = useState<CurrentUser | null>(null);
  const [isLoading, setIsLoading] = useState(() => readRefreshToken() !== null);
  const [error, setError] = useState<string | null>(null);

  const clearSession = useCallback((): void => {
    accessTokenRef.current = null;
    writeRefreshToken(null);
    setUser(null);
  }, []);

  const applyTokens = useCallback((tokens: AuthTokens): void => {
    accessTokenRef.current = tokens.accessToken;
    writeRefreshToken(tokens.refreshToken);
  }, []);

  const refreshAccessToken = useCallback(async (): Promise<string | null> => {
    const refreshToken = readRefreshToken();
    if (!refreshToken) {
      clearSession();
      return null;
    }

    const tokens = await rotateRefreshToken(refreshToken);
    if (tokens === null) {
      clearSession();
      return null;
    }

    applyTokens(tokens);
    return tokens.accessToken;
  }, [applyTokens, clearSession]);

  useEffect(() => {
    configureApiAuth({
      getAccessToken: () => accessTokenRef.current,
      refreshAccessToken,
      onUnauthorized: clearSession,
    });

    return () => {
      configureApiAuth(null);
    };
  }, [clearSession, refreshAccessToken]);

  useEffect(() => {
    let isMounted = true;

    async function boot(): Promise<void> {
      if (!readRefreshToken()) {
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      const restoredToken = await refreshAccessToken();
      if (!isMounted) return;

      if (!restoredToken) {
        setIsLoading(false);
        return;
      }

      try {
        const currentUser = await authService.me();
        if (isMounted) setUser(currentUser);
      } catch (bootError) {
        if (isMounted) {
          clearSession();
          setError(errorMessage(bootError));
        }
      } finally {
        if (isMounted) setIsLoading(false);
      }
    }

    void boot();
    return () => {
      isMounted = false;
    };
  }, [clearSession, refreshAccessToken]);

  const login = useCallback(
    async (input: LoginInput): Promise<CurrentUser> => {
      setIsLoading(true);
      setError(null);
      try {
        const tokens = await authService.login(input);
        applyTokens(tokens);
        const currentUser = await authService.me();
        setUser(currentUser);
        return currentUser;
      } catch (loginError) {
        clearSession();
        setError(errorMessage(loginError));
        throw loginError;
      } finally {
        setIsLoading(false);
      }
    },
    [applyTokens, clearSession],
  );

  const register = useCallback(
    async (input: RegisterInput): Promise<CurrentUser> => {
      setIsLoading(true);
      setError(null);
      try {
        const tokens = await authService.register(input);
        applyTokens(tokens);
        const currentUser = await authService.me();
        setUser(currentUser);
        return currentUser;
      } catch (registerError) {
        clearSession();
        setError(errorMessage(registerError));
        throw registerError;
      } finally {
        setIsLoading(false);
      }
    },
    [applyTokens, clearSession],
  );

  const refreshUser = useCallback(async (): Promise<void> => {
    try {
      const currentUser = await authService.me();
      setUser(currentUser);
    } catch {
      // A failed refresh leaves the existing user in place; a hard 401 is
      // handled by the api-client's onUnauthorized → clearSession path.
    }
  }, []);

  const logout = useCallback(async (): Promise<void> => {
    setIsLoading(true);
    setError(null);
    try {
      await authService.logout();
    } catch (logoutError) {
      setError(errorMessage(logoutError));
    } finally {
      clearSession();
      setIsLoading(false);
    }
  }, [clearSession]);

  const value = useMemo(
    () => ({
      user,
      isAuthenticated: user !== null,
      isLoading,
      error,
      login,
      register,
      logout,
      refreshUser,
      clearError: () => setError(null),
    }),
    [error, isLoading, login, logout, refreshUser, register, user],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
